import {createElement, PureComponent, PropTypes} from 'rlink-react';
import css from './ScrollEffect.css';

const RE_SCROLLING_OVERFLOW = /^(auto|scroll)$/;

export function isScrollParent (element) {
  let styles = window.getComputedStyle(element);
  return (RE_SCROLLING_OVERFLOW.test(styles.overflowX)
    || RE_SCROLLING_OVERFLOW.test(styles.overflowY));
}

export function getScrollParent (element) {
  if (!element.parentNode) {
    // must be document
    return element;
  }
  if (isScrollParent(element)) {
    return element;
  }
  return getScrollParent(element.parentNode);
}

export function onNextAnimationFrameAfterScroll (element, eventHandler) {
  let nextFrame;
  let disposable = {
    dispose () {
      window.cancelAnimationFrame(nextFrame);
      nextFrame = null;
    },
  };

  getScrollParent(element).addEventListener('scroll', ev => {
    if (!nextFrame) {
      nextFrame = window.requestAnimationFrame(ts => {
        eventHandler(ev, ts);
        nextFrame = null;
      });
    }
  });

  return disposable;
}

/*
A helper object that reports the position of an arbitrary point relative to
a container edge. Can also expose these positions as percentages of the
container (i.e. 0.0 being right up to given edge, and 1.0 the opposite edge)
and a convenience `isVisible()` method for checking if the given point is
within that given container.
 */
export class ScrollPositionHelper {
  topPercentage () {
    return this.top() / this.containerHeight();
  }
  bottomPercentage () {
    return this.bottom() / this.containerHeight();
  }
  leftPercentage () {
    return this.left() / this.containerWidth();
  }
  rightPercentage () {
    return this.right() / this.containerWidth();
  }
  isVisible () {
    return ((0 < this.topPercentage() && this.topPercentage() < 1.0)
      && (0 < this.bottomPercentage() && this.bottomPercentage() < 1.0));
  }
}

export class ViewportScrollPositionHelper extends ScrollPositionHelper {
  containerHeight () {
    return window.innerHeight;
  }
  containerWidth () {
    return window.innerWidth;
  }
}

export class PointElementScrollPositionHelper extends ViewportScrollPositionHelper {
  constructor (element) {
    super();
    this._element = element;
  }
  _rect () {
    return this._element.getBoundingClientRect();
  }
  top () {
    return this._rect().top;
  }
  bottom () {
    return this.containerHeight() - this.top();
  }
  left () {
    return this._rect().left;
  }
  right () {
    return this.containerWidth() - this.left();
  }
}

export class ElementCornerScrollPositionHelper extends PointElementScrollPositionHelper {
  constructor (element, verticalEdge, horizontalEdge) {
    super(element);
    this._verticalEdge = verticalEdge;
    this._horizontalEdge = horizontalEdge;
  }
  top () {
    return this._rect()[this._verticalEdge];
  }
  left () {
    return this._rect()[this._horizontalEdge];
  }

}

export class ElementOffsetCornerScrollPositionHelper extends ElementCornerScrollPositionHelper {
  _parentRect () {
    return this._element.offsetParent.getBoundingClientRect();
  }
  top () {
    let top = this._parentRect()[this._verticalEdge] + this._element.offsetTop;
    if (this._verticalEdge === 'bottom') {
      top = top + this._element.offsetHeight;
    }
    return top;

  }
  left () {
    let left = this._parentRect()[this._horizontalEdge] + this._element.offsetLeft;
    if (this._horizontalEdge === 'right') {
      left = left + this._element.offsetWidth;
    }
    return left;
  }
}



export default class ScrollEffect extends PureComponent {
  render () {
    return <div
      className={`${css.ScrollEffect} ${this.props.snapToEdge ? css[this.props.snapToEdge] : ''}`}
      ref={element => {
        if (element) {
          let scrollPositionHelper = new PointElementScrollPositionHelper(element);
          this.disposable = onNextAnimationFrameAfterScroll(element, () => {
            this.props.onScroll(scrollPositionHelper);
          });
        } else {
          this.disposable && this.disposable.dispose();
        }
      }} />;
  }
}

ScrollEffect.propTypes = {
  onScroll: PropTypes.func,
  snapToEdge: PropTypes.oneOf([
    'top',
    'bottom',
    'left',
    'right',
    'top-left',
    'bottom-left',
    'top-right',
    'bottom-right',
  ]),
};



// WEBPACK FOOTER //
// ./lib/rlink-transitions/ScrollEffect.jsx